/**
 * ⟦C22 · A-151⟧ `Intro` : l'animation d'ouverture de chaîne, jouée UNE FOIS, à la CRÉATION
 * seulement (jamais à la reprise) : montée par `NouvelleRun.tsx` entre le clic sur « 🎬 Lancer
 * la chaîne » et l'arrivée dans la chambre, pendant que la run se crée derrière elle.
 *
 * Porte `assets/making-of/2026-09-12-intro/A/intro.html` (variante A, télé, choix de
 * Mathieu, `docs/chantiers/C22-l-intro.md`) : même fonction `render(t)` pure recopiée
 * ci-dessous, mais rebranchée sur le vrai jeu plutôt que sur des valeurs de démo (le minutage,
 * lui, s'arrête désormais 350 ms plus tôt qu'à la maquette, 5,2 s au lieu de 5,55 s :
 * ⟦C30 · lot G⟧ ci-dessous, la maquette avait encore la boîte de Matt à la fin) :
 *
 * - le nom de chaîne tapé est celui saisi (`nomChaine`), jamais « Castelclic » en dur ;
 * - la chambre révélée à l'iris est la VRAIE scène d'arrivée ⟦A-150 · C34⟧ (`fondChambreN1`, le
 *   fond fixe de `chambre-n1` pour ce genre), le personnage y est déjà PEINT dedans, donc, à la
 *   différence de la maquette, aucun `<img class="avatar-scene">` superposé ici : ce calque-là
 *   n'a plus lieu d'être depuis ⟦A-150⟧. ⟦C34⟧ Avant ce chantier, cet écran montrait par
 *   inadvertance le mur « avancé » (liège rempli, tableau des tendances) — l'ancienne fonction ne
 *   proposait pas de variante vide sans la demander explicitement (`Accueil.tsx`, lui, la
 *   demandait). `fondChambreN1` ne connaît plus qu'un seul état, liège vide + cadres photo
 *   d'origine : celui, cohérent, d'une run qui vient de commencer — corrigé au passage, pas une
 *   régression.
 *
 * **⟦C30 · lot G⟧ Plus de boîte de Matt ICI.** Jusqu'à ce chantier, une `BoiteDialogue`
 * (`.intro-dialogue`, `REPLIQUES_BRIEFING[0]`, `instantane`) s'y fondait pendant la dernière
 * phase du minutage (l'ancienne `dialogStart`/`dialogDur`, 5100-5550 ms) — Matt, en rejouant :
 * *« il y a déjà la pop-up qui dit merci d'être venu ici, je pense qu'il faut que tu l'enlèves
 * et que la pop-up elle vient qu'au moment où la vidéo est finie, la vidéo d'intro »* (verbatim,
 * `docs/chantiers/C30-les-zones-et-le-mur.md`, lot G). La réplique se voyait deux fois : une
 * fois ici, instantanée et muette, une fois juste derrière dans `Accueil.tsx`, avec sa vraie
 * voix — exactement le défaut décrit. L'intro s'arrête maintenant sur son image finale, iris
 * grand ouvert, SANS boîte (`DUREE_FINALE_INTRO` = fin de l'ouverture de l'iris, plus bas) ;
 * `Accueil.tsx` est désormais le SEUL endroit qui joue `REPLIQUES_BRIEFING[0]`, avec sa voix et
 * son attente de clic (⟦C29 · lot D⟧), jamais une réplique dite deux fois.
 *
 * **Un tap/clic/touche « passe l'intro »** (§1 du chantier) : un seul geste, à tout instant,
 * saute à l'état final (iris grand ouvert), repris tel quel de la maquette (sauter à
 * `FINAL_T`), jamais une sortie brutale qui couperait l'animation en plein milieu.
 * `onTermine` n'est appelé qu'une fois `pret` (la run existe réellement dans le store) : si la
 * création réseau (mode compte) traîne, l'intro tient l'image finale au lieu de révéler une
 * chambre qui n'existe pas encore, jamais un écran vide.
 *
 * **`?t=<ms>` fige l'animation à une valeur donnée**, repris tel quel de `rendu.cjs` (le
 * script qui a produit les planches de la maquette validée) : sert aux captures Playwright du
 * chantier (§3 des critères), jamais utilisé en jeu normal : aucune boucle, aucun son, aucun
 * appel à `onTermine` dans ce mode.
 *
 * `prefers-reduced-motion` (lu sur `uiStore.reducedMotion`, posé par `App.tsx`) : l'intro saute
 * directement à `FINAL_T` (le fondu du fantôme est déjà fait, l'iris déjà grand ouvert, sans
 * jamais dérouler le nom tapé lettre à lettre ni le compteur), un seul tap suffit alors à
 * continuer, exactement comme en lecture complète. Le sting d'allumage sonne quand même (une
 * seule fois, au montage) ; le « clac » du compteur et le tic de frappe restent coupés, ils
 * n'ont rien à ponctuer dans cette version condensée.
 */

import { useEffect, useMemo, useRef } from "react";

import type { GenreAvatar } from "@maxime/shared";

import { LogoMeTube } from "../../components/LogoMeTube";
import { useSfx } from "../../components/SoundProvider";
import { fondChambreN1 } from "../../game/chambre";
import { nombre } from "../../game/format";
import { useUIStore } from "../../stores/uiStore";

/** Minutage (ms), IDENTIQUE à la maquette validée (`intro.html`), verrouillé, toute retouche
 *  doit être délibérée et refaire les captures du chantier.
 *
 *  ⟦C30 · lot G⟧ **`dialogStart`/`dialogDur` ont disparu d'ici** (ils valaient 5100/450 : la
 *  boîte de Matt occupait la toute dernière tranche, 5100-5550 ms). Cette phase n'existait que
 *  pour cette boîte, aujourd'hui portée par `Accueil.tsx` (voir le commentaire de tête) : sans
 *  elle, l'intro n'a plus rien à animer après l'iris, elle s'arrête donc à `irisStart + irisDur`
 *  (4350 + 850 = 5200 ms), 350 ms plus tôt qu'avant, sur son image finale. */
export const MINUTAGE_INTRO = {
  ghostEnd: 300,
  nameStart: 300,
  nameCharDur: 140,
  metaStart: 1850,
  metaDur: 250,
  metaStagger: 120,
  counterStart: 2150,
  counterDur: 900,
  clackDur: 180,
  titleExitStart: 3300,
  titleExitDur: 250,
  flashStart: 3400,
  flashDur: 90,
  logoStart: 3400,
  logoDur: 650,
  logoExitStart: 4300,
  logoExitDur: 200,
  irisStart: 4350,
  irisDur: 850,
  skipHintStart: 900,
  skipHintDur: 400,
} as const;
const T = MINUTAGE_INTRO;

/** Le temps (ms) auquel l'intro est visuellement à son état final : iris grand ouvert sur la
 *  chambre, SANS boîte de Matt (⟦C30 · lot G⟧ : la boîte ne joue plus ici, voir le commentaire
 *  de tête et celui de `MINUTAGE_INTRO` — avant ce lot, ce temps incluait encore le fondu de la
 *  boîte et valait 5550 ms). */
export const DUREE_FINALE_INTRO = T.irisStart + T.irisDur;
const FINAL_T = DUREE_FINALE_INTRO;

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
function easeOutExpo(x: number): number {
  return x >= 1 ? 1 : 1 - Math.pow(2, -10 * x);
}
function easeInOutQuad(x: number): number {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}
function progress(t: number, start: number, dur: number): number {
  return clamp01((t - start) / dur);
}

/** Le compteur, de 999 999 à 0, même mise en forme des milliers que tout le reste du jeu
 *  (`nombre()`, `game/format.ts`, `Intl.NumberFormat("fr-FR")`), avec l'accord singulier que le
 *  HUD réel n'a jamais eu besoin d'écrire (il ne redescend jamais à zéro en cours de partie ;
 *  ici la maquette compte jusque-là). Exportée pour le test. */
export function formatAboIntro(n: number): string {
  const entier = Math.max(0, Math.round(n));
  return `${nombre(entier)} ${entier <= 1 ? "abonné" : "abonnés"}`;
}

export interface IntroProps {
  /** Le nom de chaîne validé à l'étape ①, jamais tronqué, jamais « Castelclic » en dur. */
  nomChaine: string;
  genre: GenreAvatar;
  /** La run existe déjà dans le store (`runStore.creer` a résolu). Tant que c'est faux,
   *  l'intro tient l'image finale au lieu d'appeler `onTermine`. */
  pret: boolean;
  onTermine(): void;
}

export function Intro({ nomChaine, genre, pret, onTermine }: IntroProps) {
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const sfx = useSfx();

  const racineRef = useRef<HTMLDivElement>(null);
  const fantomeRef = useRef<HTMLDivElement>(null);
  const nomTexteRef = useRef<HTMLSpanElement>(null);
  const curseurNomRef = useRef<HTMLSpanElement>(null);
  const titreBlocRef = useRef<HTMLDivElement>(null);
  const ligneMetaRef = useRef<HTMLDivElement>(null);
  const compteurAboRef = useRef<HTMLSpanElement>(null);
  const crtFlashRef = useRef<HTMLDivElement>(null);
  const logoSceneRef = useRef<HTMLDivElement>(null);
  const logoRevealBoxRef = useRef<HTMLDivElement>(null);
  const chambreCapaRef = useRef<HTMLDivElement>(null);
  const skipHintRef = useRef<HTMLDivElement>(null);

  // `pret`/`onTermine`/`sfx` changent d'identité d'un rendu à l'autre (fonctions et booléens
  // normaux, pas mémoïsés côté appelant) : la boucle rAF les lit via ces refs plutôt que de les
  // lister en dépendance, pour ne JAMAIS redémarrer l'animation en cours de route : seuls
  // `nomChaine`/`genre` (fixes pour la durée de vie de l'écran) justifient un redémarrage.
  const pretRef = useRef(pret);
  pretRef.current = pret;
  const onTermineRef = useRef(onTermine);
  onTermineRef.current = onTermine;
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;

  // Figé pour les captures Playwright du chantier (§3 des critères), jamais en jeu normal.
  const tFige = useMemo(() => {
    const brut = new URLSearchParams(window.location.search).get("t");
    if (brut === null) return null;
    const n = Number(brut);
    return Number.isFinite(n) ? n : null;
  }, []);

  const lettres = useMemo(() => Array.from(nomChaine), [nomChaine]);

  // ⟦A-150⟧ La planche de la VRAIE scène d'arrivée, personnage déjà peint dedans, jamais un
  // sprite avatar superposé (l'ancien calque de la maquette n'a pas de correspondant ici).
  const chambrePlanche = fondChambreN1(genre);

  const boxWidth = useMemo(() => Math.min(220, window.innerWidth * 0.5), []);
  const boxHeight = (boxWidth * 18) / 19;

  useEffect(() => {
    const el = {
      fantome: fantomeRef.current,
      nomTexte: nomTexteRef.current,
      curseurNom: curseurNomRef.current,
      titreBloc: titreBlocRef.current,
      ligneMeta: ligneMetaRef.current,
      compteurAbo: compteurAboRef.current,
      crtFlash: crtFlashRef.current,
      logoScene: logoSceneRef.current,
      logoRevealBox: logoRevealBoxRef.current,
      chambreCapa: chambreCapaRef.current,
      skipHint: skipHintRef.current,
    };
    if (Object.values(el).some((n) => n === null)) return;

    // Le focus qui traînerait sur le bouton « 🎬 Lancer la chaîne » du formulaire encore monté
    // dessous (`NouvelleRun.tsx` le garde monté pendant que l'intro joue par-dessus) ne doit
    // jamais faire redéclencher son `submit` sur un Entrée reçu ici.
    (document.activeElement as HTMLElement | null)?.blur();

    function render(t: number) {
      // 1. Le fantôme du bouton se dissout au noir.
      const ghostP = progress(t, 0, T.ghostEnd);
      el.fantome!.style.opacity = String(1 - ghostP);
      el.fantome!.style.transform = `translateX(-50%) scale(${1 - 0.08 * ghostP})`;

      // 2. Le nom de la chaîne, lettre à lettre.
      const nameElapsed = t - T.nameStart;
      const nCh = nameElapsed < 0 ? 0 : Math.min(lettres.length, Math.floor(nameElapsed / T.nameCharDur));
      el.nomTexte!.textContent = lettres.slice(0, nCh).join("");
      const curseurVisible = t >= T.nameStart && t < T.titleExitStart && Math.floor(t / 480) % 2 === 0;
      el.curseurNom!.style.opacity = curseurVisible ? "1" : "0";

      // 3. « Jour 1 » puis le compteur d'abonnés qui claque à 0.
      const metaP = progress(t, T.metaStart, T.metaDur);
      const counterWrapP = progress(t, T.metaStart + T.metaStagger, T.metaDur);
      el.ligneMeta!.style.opacity = String(metaP);
      el.ligneMeta!.style.transform = `translateY(${10 - 10 * metaP}px)`;
      el.compteurAbo!.style.opacity = String(counterWrapP);

      const counterP = progress(t, T.counterStart, T.counterDur);
      const value = 999_999 * (1 - easeOutExpo(counterP));
      el.compteurAbo!.textContent = formatAboIntro(value);
      const counterEnd = T.counterStart + T.counterDur;
      if (t >= counterEnd && t < counterEnd + T.clackDur) {
        const cf = (t - counterEnd) / T.clackDur;
        el.compteurAbo!.style.transform = `scale(${1 + 0.22 * Math.sin(Math.PI * cf)})`;
      } else {
        el.compteurAbo!.style.transform = "scale(1)";
      }

      // Sortie du bloc titre.
      const exitP = progress(t, T.titleExitStart, T.titleExitDur);
      el.titreBloc!.style.opacity = String(1 - exitP);
      el.titreBloc!.style.transform = `translateY(${-14 * exitP}px)`;

      // 4. Le logo télé s'allume (flash cathodique, ligne blanche qui s'ouvre en logo).
      const flashP = progress(t, T.flashStart, T.flashDur);
      const flashOpacity = flashP > 0 && flashP < 1 ? Math.sin(Math.PI * flashP) * 0.85 : 0;
      el.crtFlash!.style.opacity = String(flashOpacity);

      const logoP = progress(t, T.logoStart, T.logoDur);
      const h = 2 + (boxHeight - 2) * easeInOutQuad(logoP);
      el.logoRevealBox!.style.height = `${t < T.logoStart ? 0 : h}px`;

      const logoExitP = progress(t, T.logoExitStart, T.logoExitDur);
      el.logoScene!.style.opacity = String(1 - logoExitP);

      // 5. Ouverture en iris sur la chambre : la VRAIE scène d'arrivée. Dernière phase animée de
      // l'intro (⟦C30 · lot G⟧ : la boîte de Matt qui suivait ici a été retirée, voir le
      // commentaire de tête et celui de `DUREE_FINALE_INTRO` — elle est désormais portée par
      // `Accueil.tsx`, seule et une seule fois).
      const irisP = progress(t, T.irisStart, T.irisDur);
      const radius = 150 * easeInOutQuad(irisP);
      el.chambreCapa!.style.clipPath = `circle(${radius}% at 50% 48%)`;

      // Indice de passage.
      const hintP = progress(t, T.skipHintStart, T.skipHintDur);
      el.skipHint!.style.opacity = String(0.55 * hintP);
    }

    // ─────────────── Figé pour un test (`?t=`) : un seul rendu, aucune boucle, aucun son ───────────────
    if (tFige !== null) {
      render(tFige);
      return;
    }

    let frame: number;
    let skipped = false;
    let skipTime = 0;
    let termineAppele = false;
    let allumageJoue = false;
    let clacJoue = false;
    let derniereLettreJouee = 0;
    const debut = performance.now();
    // Mouvement réduit : on part directement de l'état final (le fondu du fantôme est déjà
    // fait, l'iris déjà grand ouvert), un seul tap suffit ensuite à continuer.
    const origine = reducedMotionRef.current ? FINAL_T : 0;

    function onSkip() {
      if (skipped) return;
      skipped = true;
      skipTime = performance.now();
    }
    const racine = racineRef.current;
    racine?.addEventListener("pointerdown", onSkip);
    window.addEventListener("keydown", onSkip);

    function boucle(now: number) {
      const t = skipped ? FINAL_T + (now - skipTime) : origine + (now - debut);
      render(t);

      // Les sons de l'intro : jamais rejoués en rafale sur un saut (qui avance `t` d'un coup,
      // jamais lettre par lettre) ; coupés d'eux-mêmes si le son du jeu l'est (`useSfx`, no-op
      // silencieux). Le sting d'allumage sonne aussi sous mouvement réduit ; le clac du
      // compteur et le tic de frappe restent coupés (rien à ponctuer, l'un et l'autre ne se
      // voient jamais dans la version condensée).
      if (!allumageJoue && t >= T.flashStart) {
        allumageJoue = true;
        sfxRef.current("intro_allumage");
      }
      if (!reducedMotionRef.current) {
        const nCh = Math.min(lettres.length, Math.max(0, Math.floor((t - T.nameStart) / T.nameCharDur)));
        if (nCh > derniereLettreJouee) {
          derniereLettreJouee = nCh;
          sfxRef.current("intro_frappe_tic", { volume: 0.5 });
        }
        if (!clacJoue && t >= T.counterStart + T.counterDur) {
          clacJoue = true;
          sfxRef.current("intro_compteur_clac");
        }
      }

      if (t >= FINAL_T && pretRef.current) {
        if (!termineAppele) {
          termineAppele = true;
          onTermineRef.current();
        }
        return; // la run est prête et l'écran final est peint : plus rien à animer.
      }
      frame = requestAnimationFrame(boucle);
    }
    frame = requestAnimationFrame(boucle);

    return () => {
      cancelAnimationFrame(frame);
      racine?.removeEventListener("pointerdown", onSkip);
      window.removeEventListener("keydown", onSkip);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomChaine, genre]);

  return (
    <div className="intro-overlay" ref={racineRef} role="presentation">
      <div className="intro-chambre-capa" ref={chambreCapaRef}>
        <img className="intro-chambre-fondo pixel-asset" src={chambrePlanche} alt="" />
        <div className="intro-chambre-voile" aria-hidden="true" />
      </div>

      <div className="intro-fantome" ref={fantomeRef} aria-hidden="true">
        🎬 Lancer la chaîne
      </div>

      <div className="intro-titre-bloc" ref={titreBlocRef}>
        <div className="intro-nom-chaine">
          <span ref={nomTexteRef} />
          <span className="intro-curseur-bloc" ref={curseurNomRef} />
        </div>
        <div className="intro-ligne-meta" ref={ligneMetaRef}>
          <span className="intro-etiquette-jour">Jour 1</span>
          <span className="intro-compteur-abo" ref={compteurAboRef}>
            999 999 abonnés
          </span>
        </div>
      </div>

      <div className="intro-logo-scene" ref={logoSceneRef}>
        <div className="intro-crt-flash" ref={crtFlashRef} />
        <div className="intro-logo-reveal-box" ref={logoRevealBoxRef} style={{ width: boxWidth }}>
          <LogoMeTube width={boxWidth} height={boxHeight} />
        </div>
      </div>

      <div className="intro-scanlines" aria-hidden="true" />
      <div className="intro-vignette" aria-hidden="true" />

      <div className="intro-skip-hint" ref={skipHintRef}>
        <span>appuyer pour passer</span>
        <span className="intro-mini-fleche" />
        <span className="intro-mini-fleche" />
      </div>
    </div>
  );
}
